<template>
	<v-card class="scene-editor fill-wh" :loading="loading">
		<v-toolbar class="toolbar">
			<v-btn v-if="!!selectedScene" icon @click="selectedScene = null">
				<v-icon>fas fa-times</v-icon>
			</v-btn>

			<v-toolbar-title>
				{{ selectedScene ? selectedScene.name : "No scene selected" }}
			</v-toolbar-title>

			<v-spacer />

			<v-btn v-if="!!selectedScene" icon @click="confirmRemoveScene">
				<v-icon>fas fa-trash</v-icon>
			</v-btn>
		</v-toolbar>

		<div class="master-detail-container">
			<layout-master-detail :show-details="showDetails" :aside-size="60">
				<SceneList :items="scenes" :selected.sync="selectedScene" />

				<template v-if="selectedScene" #detail>
					<!-- check user role for advanced support -->
					<v-expansion-panels
						v-model="selectedSceneEditor"
						mandatory
						accordion
						flat
						tile
					>
						<!-- wizard accordion -->
						<v-expansion-panel>
							<v-expansion-panel-header>
								<h3>Wizard</h3>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<WizardSceneWrapper :scene="selectedScene" />
							</v-expansion-panel-content>
						</v-expansion-panel>
						<!-- advanced scene accordion -->
						<v-expansion-panel v-if="canEditScenes">
							<v-expansion-panel-header>
								<h3>Advanced Scene Editor</h3>
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<TriggerList :scene="selectedScene" />
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
				</template>
			</layout-master-detail>
		</div>

		<!-- Speed Dial -->
		<ScenesSpeedDial
			:show-add-scene="canEditScenes"
			@action:add-scene="showAddSceneDialog()"
			@action:wizard="showWizardDialog"
		/>

		<!-- Dialogs -->
		<AddSceneDialog ref="addSceneDialog" />
		<WizardSceneDialog ref="WizardDialog" />
		<dialog-confirm-dialog
			ref="confirmRemoveDialog"
			:body="strings.removeSceneConfirmation"
		/>
	</v-card>
</template>

<style lang="scss" scoped>
.master-detail-container {
	position: relative;
	min-height: calc(100% - 56px); // 56px toolbar

	@media #{map-get($display-breakpoints, 'md-and-up')} {
		min-height: calc(100% - 64px); // 64px toolbar
	}
}
</style>

<script>
import { mapActions, mapGetters } from "vuex";

import strings from "@/utils/strings";
import ability from "@/services/ability"

import TriggerList from "./scenes/TriggerList";
import SceneList from "./scenes/SceneList";
import ScenesSpeedDial from "./scenes/ScenesSpeedDial";

import AddSceneDialog from "./scenes/dialogs/AddSceneDialog";

export default {
	components: {
		TriggerList,
		SceneList,
		ScenesSpeedDial,
		AddSceneDialog,
	},

	data: () => ({
		loading: false,
		strings: strings.live.scenes,
		selectedSceneEditor: 0,
		sceneGuid: null,
	}),

	computed: {
		...mapGetters({
			system: "session/system",
		}),

		scenes() {
			return this.$store.getters["k4/scenes/scenes"];
		},

		showDetails() {
			return !!this.selectedScene;
		},

		selectedScene: {
			get() {
				let scene = this.$store.getters["k4/scenes/selectedScene"];
				if (this.sceneGuid && !scene) {
					scene = Object.values(this.scenes).find((s) => s.guid === this.sceneGuid);
				}
				return scene;
			},
			set(scene) {
				this.sceneGuid = scene ? scene.guid : null;
				const sceneName = scene ? scene.name : "";
				this.$store.commit("k4/scenes/setSelectedSceneName", sceneName);
			},
		},

		canEditScenes() {
			return ability.can("edit", "Scenes");
		},

		isWizardSelected() {
			return this.selectedSceneEditor === 0;
		},
	},

	watch: {
		async selectedScene(scene) {
			if (!scene) {
				console.log("should refresh model")
				await this.$store.dispatch("k4/controller/refreshModel");
			}
		},
	},

	beforeRouteEnter(to, from, next) {
		return next((vm) => {
			vm.loadRemoteController(vm.system.id);
		});
	},

	beforeRouteLeave(to, from, next) {
		// Make sure we disconnect from controller before leaving
		this.$store.commit("controller/resetState");
		return next();
	},

	async beforeMount() {
		this.loading = true;
		try {
			// Load descriptions
			await this.$store.dispatch("descriptions/load");
		} finally {
			this.loading = false;
		}
	},

	methods: {
		...mapActions({
			loadRemoteController: "controller/loadRemote",
			addScene: "k4/scenes/addScene",
			renameScene: "k4/scenes/renameScene",
			removeScene: "k4/scenes/removeScene",
		}),

		showAddSceneDialog() {
			this.$refs.addSceneDialog.pop(this.addScene);
		},

		showWizardDialog(wizard) {
			this.$refs.WizardDialog.pop(wizard);
		},

		async confirmRemoveScene() {
			let confirmed = await this.$refs.confirmRemoveDialog.pop();
			if (!confirmed) return;

			await this.removeScene(this.selectedScene.name);
		},
	},
};
</script>
